import {ensureScriptTag} from "../webembed/tagManager";

/**
 * Drives the ONE shared Viafoura conversation container for a liveblog.
 *
 * Viafoura scans the DOM once when its script boots, replaces every `<vf-conversations>` with its
 * own mounted widget, and offers no supported way to pick up a container added later. It also
 * does not use the custom-elements registry. A container per post therefore cannot work —
 * anything created after boot never renders.
 *
 * Since a liveblog only ever shows one conversation at a time, we keep a single container and
 * re-target it (design D15 of the add-viafoura-conversation-provider change):
 *
 *   first open  — put the container in place with the real id, THEN load the script, so Viafoura
 *                 boots straight onto it. Nothing Viafoura-related is fetched before this.
 *   later opens — swap the container and ask Viafoura to re-scan.
 *
 * The container subtree is managed imperatively and deliberately kept outside React's control,
 * because Viafoura replaces and rewrites those nodes itself.
 */

// Absolute https, not the protocol-relative form Viafoura's docs show, for two reasons:
//   - on a page served over http the protocol-relative form loads the script in cleartext, which
//     lets a network attacker inject script into the publisher's page
//   - `ensureScriptTag` de-duplicates by comparing against `HTMLScriptElement.src`, which the
//     browser has already resolved to an absolute URL. A protocol-relative string never matches,
//     so on a publisher page that already loads Viafoura we would append a SECOND entry script and
//     risk reinitialising their existing widgets. (Our own repeat opens were already guarded by
//     `scriptRequested` — this is specifically about a script the page loaded itself.)
// Matches every other third-party integration here (Twitter, Facebook, Flourish, Instagram, …).
const VIAFOURA_ENTRY_SCRIPT = "https://cdn.viafoura.net/entry/index.js";

/**
 * Viafoura's main bundle, which their entry script injects itself — we never add this.
 *
 * We only read it, as evidence that our end of the loading worked: if it is present, the entry script
 * ran and did its job, so a readiness timeout means Viafoura chose not to initialise (an
 * un-allowlisted domain) rather than that loading failed. Viafoura errors out if it is loaded twice.
 */
const VIAFOURA_MAIN_SCRIPT = "https://cdn.viafoura.net/vf-v2.js";

// Native comments already coordinate "only one conversation open at a time" through this document
// event. Reusing it means opening a Viafoura conversation closes any open native one and vice
// versa, without either side needing to know about the other.
export const SHOW_CONVERSATION_EVENT = "tikShowCommentChannelModal";

interface ViafouraContext {
  setup?: () => Promise<unknown>;
  teardown?: () => Promise<unknown>;
}

interface ViafouraApi {
  context?: ViafouraContext;
  /**
   * Viafoura's public pub/sub API — the same family their docs document for
   * `authentication`/`needed`. We use it for the tray lifecycle; see `ensureTraySubscription`.
   */
  $subscribe?: (channel: string, event: string, callback: (...args: unknown[]) => void) => void;
}

interface ViafouraWindow {
  /**
   * Viafoura's API global. Use `vf`: it is what their own supplied snippet uses and what
   * cdn.viafoura.net/entry/index.js assigns (alongside vfQ / vfConfig / vfConsent).
   *
   * `window.viafoura` also exists, but only once a second bundle has booted, and it is a different
   * object. Reading that one — as this file used to — therefore made setup()/teardown() depend on
   * timing: undefined until the late bundle arrived, so a re-target could silently skip its re-scan.
   * Measured on the localhost sandbox; see design decision D16.
   */
  vf?: ViafouraApi;
  /**
   * Viafoura's own readiness queue — their entry script pushes into it too. Callbacks run once the
   * API is live, so we never have to work out whether the script is still loading, or whether the
   * publisher's page booted Viafoura long before the liveblog appeared.
   */
  vfQ?: Array<() => void>;
}

function viafouraWindow(): ViafouraWindow {
  return window as unknown as ViafouraWindow;
}

/** `ensureScriptTag` matches on the resolved `.src`, so we do too. */
function findScriptTags(src: string): HTMLScriptElement[] {
  return Array.from(document.getElementsByTagName("script")).filter(tag => tag.src === src);
}

function viafouraContext(): ViafouraContext | undefined {
  const context = viafouraWindow().vf?.context;
  if (context) {
    ensureTraySubscription();
  }
  return context;
}

/**
 * Viafoura opens its login/profile UI in its on-site notification tray — a drawer it portals to
 * document.body, i.e. OUTSIDE the conversation overlay. Left alone, the overlay sits above it
 * (z-index) and FocusLock fights its focus management in an unbounded loop, which is exactly the
 * "log in does nothing" bug this exists to fix: the overlay must stand down while the tray is open
 * (design D1 of fix-viafoura-auth-ui-occlusion).
 *
 * `open`/`closed` is deliberately asymmetric: stand down the moment the tray STARTS opening, stand
 * back up only once it has FINISHED closing — re-engaging on `close` would yank focus out of a
 * drawer the reader can still see.
 *
 * Never unsubscribed: the tray is page-global and outlives any single overlay, like this module.
 */
let trayOpen = false;
let traySubscribed = false;

type TrayListener = (open: boolean) => void;
const trayListeners = new Set<TrayListener>();

function setTrayOpen(open: boolean) {
  if (trayOpen === open) {
    return;
  }
  trayOpen = open;
  trayListeners.forEach(listener => listener(open));
}

function ensureTraySubscription() {
  if (traySubscribed) {
    return;
  }
  const subscribe = viafouraWindow().vf?.$subscribe;
  if (!subscribe) {
    return;
  }
  traySubscribed = true;
  subscribe("tray", "open", () => setTrayOpen(true));
  subscribe("tray", "closed", () => setTrayOpen(false));
}

let trayInstallerParked = false;

/**
 * The drawer Viafoura mounts the tray into, measured on their demo site: present with a real
 * width while open, absent or zero-width otherwise. Used ONLY as a recovery probe — the events
 * above stay the mechanism — so if Viafoura ever renames it the probe degrades to "not
 * visible" and behaviour falls back to events alone.
 */
function trayVisibleInDom(): boolean {
  const tray = document.querySelector(".vf-tray-slide-wrapper");
  return !!tray && tray.getBoundingClientRect().width > 0;
}

/**
 * Re-anchor the event-derived state to reality at the moments it matters.
 *
 * Events can be missed in both directions, and each reviewer of PR #932 found one side: a
 * `closed` that never fires leaves every later conversation stood down (AWS), and resetting
 * blindly on close falsifies a tray that is genuinely still open (Codex, on the first fix).
 * Probing the DOM at the next open repairs both without guessing: a stuck-true state cannot
 * outlive the conversation it happened in, and a visible tray is never declared closed.
 *
 * Only meaningful once the event subscription exists — before that, setting the state true
 * here could never be cleared again, which is the stuck state this exists to prevent.
 */
function syncTrayStateFromDom() {
  if (!traySubscribed) {
    return;
  }
  setTrayOpen(trayVisibleInDom());
}

/**
 * Install the tray subscription as early as Viafoura allows, not just on the first open.
 *
 * Installing only when the context is first read misses a tray the reader already opened on a
 * publisher page that loads Viafoura itself — the `open` event has fired, `trayOpen` stays
 * false, and that conversation reproduces the occlusion bug until the next tray event (raised
 * by Codex on PR #932). Parking in `vfQ` costs nothing and loads nothing, so the rule that no
 * Viafoura request happens before the first open still holds on cold pages — there the queue
 * only drains once an open has loaded the script anyway.
 */
function requestTraySubscription() {
  ensureTraySubscription();
  if (traySubscribed || trayInstallerParked) {
    return;
  }
  trayInstallerParked = true;
  const w = viafouraWindow();
  w.vfQ = w.vfQ ?? [];
  w.vfQ.push(ensureTraySubscription);
}

export function isViafouraTrayOpen(): boolean {
  return trayOpen;
}

export function subscribeToViafouraTray(listener: TrayListener): () => void {
  requestTraySubscription();
  trayListeners.add(listener);
  return () => {
    trayListeners.delete(listener);
  };
}

/**
 * How long to wait for Viafoura's API before giving up on a single open. Ad blockers and strict CSPs
 * block cdn.viafoura.net outright — common on news sites — and then the queue is never drained.
 */
const VIAFOURA_READY_TIMEOUT_MS = 15000;

/**
 * Resolves with Viafoura's context once its API is live; immediately if it already is; and with
 * `undefined` if it never becomes available.
 *
 * The timeout is not cosmetic. This runs inside the serialised work queue, so a promise that never
 * settles blocks every later open AND every teardown behind it — including the DOM swap, which would
 * leave a reader clicking a second post and getting the first one's container forever. Resolving with
 * `undefined` degrades to "no conversation rendered" instead, and keeps the queue moving.
 */
function whenViafouraReady(): Promise<ViafouraContext | undefined> {
  const existing = viafouraContext();
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      // Unpark ourselves. After a timeout nobody is going to call this, and leaving it behind left
      // one dead entry in Viafoura's queue per timed-out open. Harmless individually, but it is their
      // queue, not ours, and a growing list of stale callbacks is not something to hand a vendor.
      const queue = viafouraWindow().vfQ;
      const parked = queue ? queue.indexOf(finish) : -1;
      if (parked >= 0) {
        queue!.splice(parked, 1);
      }
      resolve(viafouraContext());
    };

    const w = viafouraWindow();
    w.vfQ = w.vfQ ?? [];
    // Viafoura drains this once their API is live. If the script never arrives, nobody ever calls it.
    w.vfQ.push(finish);
    setTimeout(finish, VIAFOURA_READY_TIMEOUT_MS);
  });
}

let mountElement: HTMLElement | undefined;
let scriptRequested = false;
/**
 * The entry script tag **we** appended, if any — so recovery removes only our own.
 *
 * A publisher may have supplied the same script themselves, and their API simply being slower than
 * our readiness timeout must not cost them their tag: deleting it while its request may still be in
 * flight invites a double boot and disturbs their other widgets. We are only entitled to remove what
 * we added.
 */
let ownScriptTag: HTMLScriptElement | undefined;
let currentContainerId: string | undefined;
let widgetActive = false;

/**
 * What the last successful render produced: which container id, in which mount node.
 *
 * This replaces `!widgetActive` as the "is there work to do?" test on mount registration. The flag only
 * says *a* widget exists somewhere, which stopped being enough once a page could have two embeds: on a
 * switch from one embed to the other the widget is active but lives in the node that just detached, and
 * the newly registered mount then got no render at all — measured as an open conversation with no
 * container. Comparing the pair also makes a double-enqueue harmless, which is what `!widgetActive`
 * was guarding against in the first place (design D21a).
 */
let rendered: {mount: HTMLElement, containerId: string} | undefined;

// teardown/setup are async, and a reader can click a second post's trigger before the first
// sequence finishes. Serialising keeps teardown → replace → setup from interleaving.
let queue: Promise<unknown> = Promise.resolve();
function enqueue<T>(work: () => Promise<T>): Promise<T> {
  const next = queue.then(work, work);
  queue = next.catch(() => undefined);
  return next;
}

/**
 * Viafoura's documented sequence for re-targeting a widget without a page load is
 * teardown → change DOM → setup. Skipping the teardown looks fine but leaks state across opens:
 * it is what flushes the closing conversation's engagement analytics, closes its realtime
 * subscriptions and unregisters the old widget.
 */
async function teardownCurrentWidget() {
  if (!widgetActive) {
    return;
  }
  widgetActive = false;
  rendered = undefined;
  try {
    await viafouraContext()?.teardown?.();
  } catch {
    // Never let a teardown failure block opening or closing the overlay.
  }
}

type Listener = (containerId: string | undefined) => void;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach(listener => listener(currentContainerId));
}

export function subscribeToViafouraConversation(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The shared overlay registers the node Viafoura is allowed to own.
 *
 * The overlay only exists while it is open, so this arrives *after* the open that caused it. When a
 * mount shows up while a conversation is pending, that open's work starts here (design D21).
 */
export function registerViafouraMount(element: HTMLElement | undefined) {
  mountElement = element;
  const alreadyRendered = !!rendered && rendered.mount === element && rendered.containerId === currentContainerId;
  if (element && currentContainerId && !alreadyRendered) {
    renderCurrentConversation(currentContainerId);
  }
}

/**
 * Give the mount node back, but only if it is still the one the controller holds.
 *
 * `registerViafouraMount(undefined)` would clear whatever is registered, which is wrong as soon as a
 * page has two liveblog embeds: the sidebar that is detaching may already have been superseded by the
 * other embed's, and clearing it then takes the mount out from under a conversation that is opening.
 * Raised by Codex on PR #886, following the ownership fix in D24.
 */
export function releaseViafouraMount(element: HTMLElement) {
  if (mountElement === element) {
    mountElement = undefined;
  }
}

export function currentViafouraContainerId(): string | undefined {
  return currentContainerId;
}

/**
 * Point the shared container at `containerId` and make Viafoura render it.
 *
 * Records the request unconditionally: the overlay is what provides the mount node, and it only
 * mounts once this has notified subscribers. So an open with no mount yet is normal, not a failure —
 * `registerViafouraMount` picks the work up when the node appears (design D21).
 */
export function openViafouraConversation(containerId: string): void {
  currentContainerId = containerId;
  // Before the overlay decides its stacking and focus posture for this open (design D7).
  syncTrayStateFromDom();

  // Read BEFORE notify(), because notify() is what can mount the overlay — synchronously, in the same
  // microtask. This one line is what makes the two render paths mutually exclusive by construction
  // rather than by timing:
  //
  //   mounted already  → the overlay is open and we are switching posts. registerViafouraMount will
  //                      not fire again (the node does not change), so this call must do the work.
  //   not mounted yet  → the overlay is about to appear, and its mount registration does the work —
  //                      whether that happens during notify() or a tick later.
  //
  // Both paths used to enqueue, on the reasoning that "whichever comes second does the work; the
  // first finds no mount and returns". That held only while the mount arrived after our own queued
  // job had run, and it does not: notify() schedules React's work before enqueue() schedules ours,
  // and a concurrent root flushes a sync-lane update — a click is one — in a microtask. So the portal
  // was committed before our job looked at `mountElement`, both jobs proceeded, and the very first
  // open ran setup → teardown → setup: the widget visibly rebuilt, and two lifecycles' worth of work
  // billed to Viafoura. Raised by Codex on PR #886 and reproduced in
  // `viafouraConversation.test.ts` — note that driving it through `act()` flushes in the favourable
  // order and reports the bug as absent, so the reproduction deliberately avoids React.
  const alreadyMounted = !!mountElement;

  // Closes any open native comment thread, and tells other Viafoura previews they are not active.
  // This is also what makes the overlay open, and therefore what produces the mount node.
  document.dispatchEvent(new CustomEvent(SHOW_CONVERSATION_EVENT, {detail: containerId}));
  notify();

  if (alreadyMounted) {
    renderCurrentConversation(containerId);
  }
}

/**
 * Drives Viafoura's teardown → replace → setup sequence for `containerId`.
 *
 * Entered from either side of the mount race: the open, if the overlay is already mounted, or
 * `registerViafouraMount` once it is. Whichever comes second does the work; the first one finds no
 * mount and returns.
 */
function renderCurrentConversation(containerId: string) {
  void enqueue(async () => {
    if (!mountElement) {
      return;
    }

    // Already done, in this exact node, for this exact conversation. Reachable when both entry points
    // queue work — which the mount race across two embeds can produce whichever way it resolves — and
    // repeating it would rebuild the widget in front of the reader (design D21a).
    if (rendered && rendered.mount === mountElement && rendered.containerId === containerId) {
      return;
    }

    await teardownCurrentWidget();

    // Replacing the container is what makes Viafoura re-target it — setting `vf-container-id` on
    // the already-mounted widget has no effect.
    mountElement.replaceChildren();
    const container = document.createElement("vf-conversations");
    container.setAttribute("vf-container-id", containerId);
    mountElement.appendChild(container);

    if (!scriptRequested) {
      scriptRequested = true;
      // A no-op when the publisher's page already loaded Viafoura — which is exactly why we cannot
      // then rely on a boot scan (see below).
      // crossOrigin matches the Twitter/Flourish pattern here; verified safe because
      // cdn.viafoura.net serves `access-control-allow-origin: *`, so the CORS fetch succeeds.
      // referrerPolicy is deliberately NOT set — Viafoura keys sites off the domain, and this is
      // the one behaviour we cannot verify without an allowlisted customer domain (task 11.4), so
      // stripping a header they might rely on is a risk with no upside here.
      const preexisting = findScriptTags(VIAFOURA_ENTRY_SCRIPT).length > 0;
      ensureScriptTag({src: VIAFOURA_ENTRY_SCRIPT, async: true, crossOrigin: "anonymous"});
      // Only claim ownership when there was nothing there before — otherwise the tag is the
      // publisher's and is not ours to remove.
      ownScriptTag = preexisting ? undefined : findScriptTags(VIAFOURA_ENTRY_SCRIPT)[0];
    }

    // Always wait for the API and call setup(), instead of assuming a pending boot scan will claim
    // the container. The scan only helps on a cold page; on a publisher page that booted Viafoura
    // before the liveblog loaded, the scan is long finished and setup() is the ONLY thing that
    // renders the conversation. Viafoura describe setup() as a soft re-init, so calling it on a
    // cold page as well is harmless.
    const context = await whenViafouraReady();

    if (!context) {
      // Readiness timed out — but for one of two very different reasons, and only one of them is
      // worth retrying. Treating them alike made Viafoura load its main bundle twice and throw
      // "page has loaded vf-v2.js 1 extra time. This is a customer implementation problem".
      //
      //  a) our entry script never got far enough to pull Viafoura's main bundle — blocked by an ad
      //     blocker or a strict CSP, or the network failed. A later open should genuinely retry, so
      //     reset the loader state and drop our dead tag.
      //  b) the bundle loaded fine and Viafoura simply never became ready. That is what an
      //     un-allowlisted domain looks like, i.e. the state EVERY new customer starts in until
      //     their domains are cleared with Viafoura. Retrying cannot fix a configuration problem —
      //     and removing our entry tag does not un-inject the `vf-v2.js` it already added, so
      //     re-adding it later loads that bundle a second time.
      if (findScriptTags(VIAFOURA_MAIN_SCRIPT).length === 0) {
        scriptRequested = false;
        ownScriptTag?.remove();
        ownScriptTag = undefined;
      }
      // Same reasoning as the mismatch guard below, and it was missing here: our
      // `<vf-conversations>` has been in the DOM since a few lines up, so a bundle that boots LATE —
      // after this 15 s timeout — can still have its scan claim the node. Leaving `widgetActive`
      // false would make the next close or post switch skip `teardown()` entirely, leaving a live
      // widget's subscriptions open and its closing analytics unflushed. Raised by Codex on PR #886.
      //
      // Marking it active rather than removing the container is deliberate: if Viafoura does boot
      // late, the reader still gets their conversation, and the close path now tears it down. Where
      // no widget ever appeared the teardown is a no-op — `viafouraContext()?.teardown?.()` is
      // optional all the way through.
      widgetActive = true;
      return;
    }

    // The reader may have closed the overlay or opened a different post while we waited.
    if (currentContainerId !== containerId) {
      // Do NOT return as "nothing mounted". Our `<vf-conversations>` is still in the DOM if the
      // reader closed rather than switched, and a cold entry script's boot scan can still claim it —
      // leaving a live widget with open subscriptions behind a closed overlay that no later close
      // would tear down, because it would see widgetActive === false. Mark it so the queued close
      // does its job.
      widgetActive = true;
      return;
    }

    await context.setup?.();
    widgetActive = true;
    rendered = {mount: mountElement, containerId};
  });
}

export function closeViafouraConversation() {
  currentContainerId = undefined;
  // Deliberately does NOT touch the tray state: a conversation can close underneath a tray
  // that is genuinely still open (a live update removing the post, say), and declaring the
  // tray closed then would put the next overlay on top of it again — no new `open` event is
  // coming (raised by Codex on PR #932, round 2). Recovery from a LOST `closed` event happens
  // in `syncTrayStateFromDom` at the next open instead.
  // Order, stated honestly, because a review read the previous comment as a guarantee it is not:
  // `notify()` drives React, `enqueue` schedules the teardown, and the teardown therefore usually
  // runs AFTER the overlay's markup has already detached. `enqueue` never runs work synchronously, so
  // swapping these two lines would not change that.
  //
  // That is acceptable, and measured rather than assumed: teardown() on a detached subtree resolves
  // and restores the placeholder (design D17). What we deliberately do NOT do is await the teardown
  // before closing — the queue may hold an earlier open whose readiness wait runs to 15 s, and a
  // reader's close must never sit behind that (design D18 revisited).
  notify();
  void enqueue(async () => {
    const wasActive = widgetActive;
    await teardownCurrentWidget();
    // teardown() is page-global — it unregisters EVERY Viafoura widget on the page, not just our
    // conversation (measured; task 11.9 of add-viafoura-conversation-provider). The post-switch
    // path recovers because its teardown is followed by setup(); a close without one left the
    // publisher's own widgets dead — on the demo page the header's tray-trigger bell survived as
    // an empty 0×0 element. So a close is Viafoura's re-target sequence pointed at "no
    // conversation": the re-scan re-mounts their widgets, and cannot resurrect ours because the
    // container detached with the overlay (design D6).
    //
    // Skipped when nothing was torn down, and when a reopen has already been recorded — its render
    // job ends in setup() anyway, so the page's widgets are revived without a double re-init.
    if (!wasActive || currentContainerId !== undefined) {
      return;
    }
    try {
      await viafouraContext()?.setup?.();
    } catch {
      // Reviving the page's widgets must never block or fail the close.
    }
  });
}

export function resetViafouraConversationForTests() {
  mountElement = undefined;
  scriptRequested = false;
  ownScriptTag = undefined;
  currentContainerId = undefined;
  widgetActive = false;
  rendered = undefined;
  queue = Promise.resolve();
  listeners.clear();
  trayOpen = false;
  traySubscribed = false;
  trayInstallerParked = false;
  trayListeners.clear();
}
